import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import RadioButtonInput from '../components/Inputs/RadioButtonInput/RadioButtonInput';
import TextInput from '../components/Inputs/TextInput/TextInput';
import useListOfForms from './useListOfForms';
import { selectAppConfig } from '../redux/appConfigReducer';
import { selectInputs } from '../redux/inputsReducer';
import DropDownSelector from '../components/Inputs/DropdownInput/DropDownSelector';
import VehicleDropdown from '../components/Inputs/DropdownInput/vehicleDropDown';
import useInputOptions from './useInputOptions';
import { GROUPED_INPUTS, INPUT_TYPE } from '../constants';
import useSourceOfLead from './useSourceOfLead';
import { LIST_OF_VEHICLES } from '../constants/vehicles';
import DateSelector from '../components/Inputs/DateSelector';
import GroupInputs, { SmallGroupInputs } from '../components/GroupInputs';
import { getOptionsByCompositeCodes } from '../utils/vehicleFormattor';
import CheckboxSelector from '../components/CheckboxSelector/CheckboxSelector';

// Should be the main hook that will allow to build components.
// We should import here all the JSX individual components
// And write logic where based on the type of form - we are returning back prepared and ready to use lead form
// First we will call useListOfForms, and pass there name of the requested form.
// Then we will get back array with components that should render. Based on that array we will map and render
// Components we want to see on the page, and we will send it back to the main DigitalLeads.tsx

type LeadInput = {
  inputKey: string;
  Component: JSX.Element;
};

const isAlpha = !process.env.IS_LEGACY;

const useLeadsBuilder = () => {
  // Grab LF name from Redux
  // Pass it to useListForms and get back list of components need to render
  const { t } = useTranslation();
  const {
    TOPIC_OF_INTEREST,
    PREFERRED_CONTACT_METHOD,
    TIER3_TOPIC_OF_INTEREST,
    ALL_TOPIC_OF_INTEREST,
    CONTACT_TIME,
  } = useInputOptions();

  const { presetForm, customForm, vehicleId, market, topicOfInterests, modelSelect, handraiser } =
    useSelector(selectAppConfig);

  const { modelFamilyOfInterest: modelFamilyOfInterestSelection, preferredContactMethod } =
    useSelector(selectInputs);

  const isUSMarket = market?.toLowerCase().includes('us');

  const { requestSource } = useSourceOfLead();

  const hasOptionalField = presetForm === 'tradeIn';

  const listOfInputs = useListOfForms(presetForm);

  const isTier1 = requestSource === 'tier_1';

  const topicOfInterestListToRender = useMemo(() => {
    if (topicOfInterests.length)
      return Object.fromEntries(
        Object.entries(ALL_TOPIC_OF_INTEREST).filter(([key]) => topicOfInterests.includes(key)),
      );
    if (isTier1) return TOPIC_OF_INTEREST;
    return TIER3_TOPIC_OF_INTEREST;
  }, [
    ALL_TOPIC_OF_INTEREST,
    TIER3_TOPIC_OF_INTEREST,
    TOPIC_OF_INTEREST,
    isTier1,
    topicOfInterests,
  ]);

  // -----------------------------------------------------------------------------------------------//
  // ------------------------------------ Individual Inputs ----------------------------------------//
  // -----------------------------------------------------------------------------------------------//

  const topicOfInterest = useMemo(() => {
    const label = isAlpha
      ? t('digitalLeads.inputs.topicOfInterest.placeHolder')
      : `${t('digitalLeads.inputs.topicOfInterest.placeHolder')} *`;

    return {
      inputKey: INPUT_TYPE.TOPIC_OF_INTEREST,
      Component: (
        <DropDownSelector
          options={topicOfInterestListToRender}
          title={t('digitalLeads.inputs.topicOfInterest.title')}
          inputLabel={label}
          inputKey={INPUT_TYPE.TOPIC_OF_INTEREST}
          dropdownHeight={requestSource === 'tier_1' ? 260 : 392}
        />
      ),
    };
  }, [topicOfInterestListToRender, t, requestSource]);

  const firstNameInput = useMemo(() => {
    let label = t('digitalLeads.inputs.firstNamePlaceHolder');
    if (!hasOptionalField && !isAlpha) {
      label = `${label} *`;
    }

    return {
      inputKey: INPUT_TYPE.FIRST_NAME,
      Component: <TextInput inputLabel={label} inputKey={INPUT_TYPE.FIRST_NAME} inputType="text" />,
    };
  }, [hasOptionalField, t]);

  const lastNameInput = useMemo(() => {
    let label = t('digitalLeads.inputs.lastNamePlaceHolder');
    if (!hasOptionalField && !isAlpha) {
      label = `${label} *`;
    }
    return {
      inputKey: INPUT_TYPE.LAST_NAME,
      Component: <TextInput inputLabel={label} inputKey={INPUT_TYPE.LAST_NAME} inputType="text" />,
    };
  }, [hasOptionalField, t]);

  const zipInput = useMemo(() => {
    let label = isUSMarket
      ? t('digitalLeads.inputs.zipCodePlaceHolder')
      : t('digitalLeads.inputs.postalCodePlaceHolder');
    if (!hasOptionalField && !isAlpha) {
      label = `${label} *`;
    }

    return {
      inputKey: INPUT_TYPE.ZIP,
      Component: (
        <TextInput
          inputLabel={label}
          inputKey={INPUT_TYPE.ZIP}
          inputType="text"
          inputNote={
            isUSMarket ? t('digitalLeads.inputs.zipNote') : t('digitalLeads.inputs.postalNote')
          }
        />
      ),
    };
  }, [hasOptionalField, isUSMarket, t]);

  const emailInput = useMemo(() => {
    let label = t('digitalLeads.inputs.emailPlaceHolder');
    if (!hasOptionalField && !isAlpha) {
      label = `${label} *`;
    }

    return {
      inputKey: INPUT_TYPE.EMAIL,
      Component: (
        <TextInput
          inputLabel={label}
          inputKey={INPUT_TYPE.EMAIL}
          inputType="email"
          inputNote={!handraiser ? t('digitalLeads.inputs.emailNote') : undefined}
        />
      ),
    };
  }, [hasOptionalField, t, handraiser]);

  const phoneNumberInput = useMemo(() => {
    const isOptional = preferredContactMethod === INPUT_TYPE.EMAIL;
    const label =
      isOptional || isAlpha
        ? t('digitalLeads.inputs.phonePlaceHolder')
        : `${t('digitalLeads.inputs.phonePlaceHolder')} *`;
    return {
      inputKey: INPUT_TYPE.PHONE,
      Component: (
        <TextInput
          inputLabel={label}
          inputKey={INPUT_TYPE.PHONE}
          inputType="text"
          isOptional={isOptional}
        />
      ),
    };
  }, [preferredContactMethod]);

  const preferedContact = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.PREFERED_METHOD,
      Component: (
        <RadioButtonInput
          inputKey={INPUT_TYPE.PREFERED_METHOD}
          options={PREFERRED_CONTACT_METHOD}
        />
      ),
    };
  }, [PREFERRED_CONTACT_METHOD]);

  const preferedContactTime = useMemo(() => {
    const isPhoneInput = preferredContactMethod !== INPUT_TYPE.EMAIL;
    if (!isPhoneInput) return null;
    return {
      inputKey: INPUT_TYPE.CONTACT_TIME,
      Component: (
        <DropDownSelector
          options={CONTACT_TIME}
          inputLabel="Preferred contact time"
          inputKey={INPUT_TYPE.CONTACT_TIME}
          inputNote="While your dealer will do their best to accommodate, your chosen time is not guaranteed."
          isOptional
          dropdownHeight={176}
        />
      ),
    };
  }, [CONTACT_TIME, preferredContactMethod]);

  const modelFamilyOfInterest = useMemo(() => {
    const modelFamilyList = Object.keys(LIST_OF_VEHICLES);
    return {
      inputKey: INPUT_TYPE.MODEl_FAMILY_OF_INTEREST,
      Component: (
        <VehicleDropdown
          options={modelFamilyList}
          inputLabel={`${t('digitalLeads.inputs.modelFamilyOfInterestPlaceHolder')}`}
          inputKey={INPUT_TYPE.MODEl_FAMILY_OF_INTEREST}
          dropdownHeight={350}
        />
      ),
    };
  }, [t]);

  const modelLineOfInterest = useMemo(() => {
    const options =
      LIST_OF_VEHICLES[modelFamilyOfInterestSelection as keyof typeof LIST_OF_VEHICLES]?.options ||
      [];
    return {
      inputKey: INPUT_TYPE.MODEl_LINE_OF_INTEREST,
      Component: (
        <VehicleDropdown
          options={options}
          inputLabel={t('digitalLeads.inputs.modelLineOfInterestPlaceHolder')}
          inputKey={INPUT_TYPE.MODEl_LINE_OF_INTEREST}
          dropdownHeight={350}
        />
      ),
    };
  }, [modelFamilyOfInterestSelection, t]);

  const customModelLineOfInterest = useMemo(() => {
    const options = getOptionsByCompositeCodes(modelSelect || []) || [];
    const optionLabels = options.map((option) => option.label);
    return {
      inputKey: INPUT_TYPE.MODEl_LINE_SELECT_CUSTOM,
      Component: (
        <GroupInputs title={t('digitalLeads.inputs.modelSelection')}>
          <VehicleDropdown
            options={optionLabels}
            inputLabel={t('digitalLeads.inputs.modelLineOfInterestPlaceHolder')}
            inputKey={INPUT_TYPE.MODEl_LINE_SELECT_CUSTOM}
            dropdownHeight={350}
          />
        </GroupInputs>
      ),
    };
  }, [t, modelSelect]);

  const checkboxesModelLineOfInterest = useMemo(() => {
    const options =
      getOptionsByCompositeCodes((modelSelect || []).filter((code) => code !== 'AUA000_AUA000')) ||
      [];
    return {
      inputKey: INPUT_TYPE.MODEL_LINES_SELECTED,
      Component: (
        <GroupInputs title={t('digitalLeads.inputs.modelSelection')}>
          <CheckboxSelector
            options={options}
            inputKey={INPUT_TYPE.MODEL_LINES_SELECTED}
            maxSelection={3}
          />
        </GroupInputs>
      ),
    };
  }, [t, modelSelect]);

  const tradeInVehicleYear = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_YEAR,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleYear')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_YEAR}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleMake = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_MAKE,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleMake')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_MAKE}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleModel = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_MODEL,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleModel')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_MODEL}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleMileage = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_MILEAGE,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleMileage')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_MILEAGE}
          inputType="text"
        />
      ),
    };
  }, [t]);

  const tradeInVehicleVin = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TRADE_IN_VEHICLE_VIN,
      Component: (
        <TextInput
          inputLabel={t('digitalLeads.inputs.tradeInVehicleVin')}
          inputKey={INPUT_TYPE.TRADE_IN_VEHICLE_VIN}
          inputType="text"
          inputNote={t('digitalLeads.inputs.vinNote')}
          helperIcon
          isOptional
          popoverDescription={t('digitalLeads.vinPopover.description')}
          popoverTitle={t('digitalLeads.vinPopover.title')}
        />
      ),
    };
  }, [t]);

  const datePickerInput = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.DATE_PICKER,
      Component: <DateSelector inputKey={INPUT_TYPE.DATE_PICKER} />,
    };
  }, []);

  const preferredBookingTime = useMemo(() => {
    return {
      inputKey: INPUT_TYPE.TEST_DRIVE_BOOKING_TIME,
      Component: (
        <DropDownSelector
          options={CONTACT_TIME}
          inputLabel="What is your preferred booking time?"
          inputKey={INPUT_TYPE.TEST_DRIVE_BOOKING_TIME}
          dropdownHeight={176}
        />
      ),
    };
  }, [CONTACT_TIME]);

  // -----------------------------------------------------------------------------------------------//
  // ------------------------------------- Grouped Inputs ------------------------------------------//
  // -----------------------------------------------------------------------------------------------//

  const contactMethod = useMemo(() => {
    return {
      inputKey: GROUPED_INPUTS.CONTACT_METHOD,
      Component: (
        <SmallGroupInputs title={t('digitalLeads.inputs.preferredContactMethod.label')}>
          {preferedContact.Component}
          {emailInput.Component}
          {phoneNumberInput.Component}
          {preferedContactTime?.Component}
        </SmallGroupInputs>
      ),
    };
  }, [
    emailInput.Component,
    phoneNumberInput.Component,
    preferedContact.Component,
    preferedContactTime?.Component,
    t,
  ]);

  const contactInformation = useMemo(() => {
    return {
      inputKey: GROUPED_INPUTS.CONTACT_INFORMATION,
      Component: (
        <GroupInputs title={t('digitalLeads.contactInfoLabel')}>
          {firstNameInput.Component}
          {lastNameInput.Component}
          {zipInput.Component}
          <SmallGroupInputs title={t('digitalLeads.inputs.preferredContactMethod.label')}>
            {preferedContact.Component}
            {emailInput.Component}
            {phoneNumberInput.Component}
            {preferedContactTime?.Component}
          </SmallGroupInputs>
        </GroupInputs>
      ),
    };
  }, [
    emailInput.Component,
    firstNameInput.Component,
    lastNameInput.Component,
    phoneNumberInput.Component,
    preferedContact.Component,
    preferedContactTime?.Component,
    t,
    zipInput.Component,
  ]);

  const basicHandraiserGroup = useMemo(() => {
    return {
      inputKey: GROUPED_INPUTS.BASIC_HANDRAISER,
      Component: (
        <GroupInputs title={t('digitalLeads.contactInfoLabel')}>
          {firstNameInput.Component}
          {lastNameInput.Component}
          {zipInput.Component}
          {emailInput.Component}
          {phoneNumberInput.Component}
        </GroupInputs>
      ),
    };
  }, [
    t,
    emailInput.Component,
    firstNameInput.Component,
    lastNameInput.Component,
    phoneNumberInput.Component,
    zipInput.Component,
  ]);

  const tradeInVehicleInputs = useMemo(() => {
    return {
      inputKey: GROUPED_INPUTS.TRADE_IN_VEHICLE,
      Component: (
        <GroupInputs title={t('digitalLeads.inputs.vehicleDetails')}>
          {tradeInVehicleMake.Component}
          {tradeInVehicleModel.Component}
          {tradeInVehicleYear.Component}
          {tradeInVehicleMileage.Component}
          {tradeInVehicleVin.Component}
        </GroupInputs>
      ),
    };
  }, [
    t,
    tradeInVehicleMake.Component,
    tradeInVehicleMileage.Component,
    tradeInVehicleModel.Component,
    tradeInVehicleVin.Component,
    tradeInVehicleYear.Component,
  ]);

  const modelOfInterestInputs = useMemo(() => {
    return {
      inputKey: GROUPED_INPUTS.MODEL_OF_INTEREST,
      Component: (
        <GroupInputs title={t('digitalLeads.inputs.modelSelection')}>
          {modelFamilyOfInterest.Component}
          {modelLineOfInterest.Component}
        </GroupInputs>
      ),
    };
  }, [modelFamilyOfInterest.Component, modelLineOfInterest.Component, t]);

  const testDriveInputs = useMemo(() => {
    return {
      inputKey: GROUPED_INPUTS.TEST_DRIVE_BOOK,
      Component: (
        <GroupInputs title={t('digitalLeads.inputs.testDriveLabel')}>
          {datePickerInput.Component}
          {preferredBookingTime.Component}
        </GroupInputs>
      ),
    };
  }, [datePickerInput.Component, preferredBookingTime.Component, t]);

  // If we are adding new JSX component as a variable here - need to add this var in the array below

  const isLeadInput = (x: LeadInput | null | undefined): x is LeadInput => !!x;

  const fullListOfAllInputs = useMemo(() => {
    return [
      firstNameInput,
      lastNameInput,
      zipInput,
      preferedContact,
      emailInput,
      phoneNumberInput,
      preferedContactTime,
      topicOfInterest,
      contactInformation,
      contactMethod,
      tradeInVehicleInputs,
      modelOfInterestInputs,
      testDriveInputs,
      customModelLineOfInterest,
      checkboxesModelLineOfInterest,
      basicHandraiserGroup,
    ].filter(isLeadInput);
  }, [
    firstNameInput,
    lastNameInput,
    zipInput,
    preferedContact,
    emailInput,
    phoneNumberInput,
    preferedContactTime,
    topicOfInterest,
    tradeInVehicleInputs,
    modelOfInterestInputs,
    testDriveInputs,
    contactInformation,
    contactMethod,
    customModelLineOfInterest,
    checkboxesModelLineOfInterest,
    basicHandraiserGroup,
  ]);

  const buildedForm = useMemo(() => {
    if (customForm?.length)
      return fullListOfAllInputs
        .filter((input) => customForm.includes(input.inputKey))
        .sort((a, b) => customForm.indexOf(a.inputKey) - customForm.indexOf(b.inputKey));
    if (presetForm) {
      return fullListOfAllInputs
        .filter((input) => listOfInputs?.includes(input.inputKey))
        .sort(
          (a, b) =>
            (listOfInputs?.indexOf(a.inputKey) ?? 0) - (listOfInputs?.indexOf(b.inputKey) ?? 0),
        );
    }
    return fullListOfAllInputs
      .filter((input) => listOfInputs?.includes(input.inputKey))
      .sort(
        (a, b) =>
          (listOfInputs?.indexOf(a.inputKey) ?? 0) - (listOfInputs?.indexOf(b.inputKey) ?? 0),
      );
  }, [customForm, fullListOfAllInputs, listOfInputs, presetForm]);

  return { buildedForm };
};

export default useLeadsBuilder;
